import { KeyMapItem, KeyConfig, DEFAULT_KEY_CONFIG } from "../@types/keyboard";

export function getKeyDisplay(item: KeyMapItem): string {
  if (item.display) return item.display;
  const map: Record<string, string> = {
    ArrowUp: "↑",
    ArrowDown: "↓",
    ArrowLeft: "←",
    ArrowRight: "→",
    Backspace: "⌫",
    Enter: "↵",
    " ": "Space",
    Escape: "Esc",
    Tab: "Tab",
  };
  return map[item.key] ?? item.key.toUpperCase();
}

export function findConflict(config: KeyConfig, id: string, key: string): KeyMapItem | undefined {
  for (const items of Object.values(config)) {
    const found = (items as KeyMapItem[]).find((i) => i.id !== id && i.key === key);
    if (found) return found;
  }
  return undefined;
}

export function hasConflicts(config: KeyConfig): boolean {
  for (const items of Object.values(config)) {
    for (const item of items as KeyMapItem[]) {
      if (findConflict(config, item.id, item.key)) return true;
    }
  }
  return false;
}

interface StoredKeyConfig {
  labeling: string[];
  nav: {
    prevImage: string;
    nextImage: string;
  };
  action: {
    reset: string;
    undo: string | { key: string; modifier?: string };
    save: string | { key: string; modifier?: string };
    selectAll: string | { key: string; modifier?: string };
  };
}

export function parseKeyConfig(configStr: string): KeyConfig {
  try {
    const parsed = JSON.parse(configStr);
    const stored: StoredKeyConfig = parsed.keyConfig;

    if (!stored) return JSON.parse(JSON.stringify(DEFAULT_KEY_CONFIG));

    const resolveAction = (val: string | { key: string; modifier?: string }) =>
      typeof val === "string" ? { key: val, modifier: undefined } : val;

    const result: KeyConfig = JSON.parse(JSON.stringify(DEFAULT_KEY_CONFIG));

    // labeling — 배열 순서대로 매핑
    stored.labeling?.forEach((key, i) => {
      if (result.labeling[i]) {
        result.labeling[i].key = key;
        result.labeling[i].display = key.toUpperCase();
      }
    });

    // navigation
    if (stored.nav) {
      result.navigation[0].key = stored.nav.prevImage;
      result.navigation[1].key = stored.nav.nextImage;
    }

    // action
    if (stored.action) {
      const actionMap = [
        { idx: 0, val: stored.action.reset },
        { idx: 1, val: stored.action.undo },
        { idx: 2, val: stored.action.save },
        { idx: 3, val: stored.action.selectAll },
      ];
      actionMap.forEach(({ idx, val }) => {
        if (!val) return;
        const { key, modifier } = resolveAction(val);
        result.action[idx].key = key;
        if (modifier) result.action[idx].modifier = modifier as "Ctrl" | "Alt";
        else delete result.action[idx].modifier;
      });
    }

    return result;
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_KEY_CONFIG));
  }
}

// useKeyMapping의 KeyConfig → DB 저장용 string으로 변환
export function serializeKeyConfig(config: KeyConfig): string {
  const stored: StoredKeyConfig = {
    labeling: config.labeling.map((i) => i.key),
    nav: {
      prevImage: config.navigation[0].key,
      nextImage: config.navigation[1].key,
    },
    action: {
      reset: config.action[0].key,
      undo: config.action[1].modifier
        ? { key: config.action[1].key, modifier: config.action[1].modifier }
        : config.action[1].key,
      save: config.action[2].modifier
        ? { key: config.action[2].key, modifier: config.action[2].modifier }
        : config.action[2].key,
      selectAll: config.action[3].modifier
        ? { key: config.action[3].key, modifier: config.action[3].modifier }
        : config.action[3].key,
    },
  };

  // 기존 config 필드의 다른 값들은 유지하면서 keyConfig만 교체
  return JSON.stringify({ keyConfig: stored });
}
